import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';

import { fromJS, List } from 'immutable';

import { logErrorToServer } from 'app/common/ErrorBoundary';
import Image, { styles as imageStyles } from './image';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Cell, Grid } from 'react-flexr';

import Button from '@mui/material/Button';

import i18n from 'app/common/i18n';

class InputImages extends Component {
  static propTypes = {
    onLoading: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    upload: PropTypes.func.isRequired,
    files: PropTypes.instanceOf(List).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      files: (props.files || fromJS([])).filter(Boolean)
    };

    this._pending = new Set();
    this.dropzoneRef = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.files !== nextProps.files) {
      this.setState({ files: nextProps.files.filter(Boolean) });
    }
  }

  _moveCard(dragIndex, hoverIndex) {
    const { files } = this.state;
    const dragCard = files.get(dragIndex);
    return this.updateState(
      files.remove(dragIndex).insert(hoverIndex, dragCard)
    );
  }

  _loading = (key, done) => {
    if (done) {
      this._pending.delete(key);
    } else {
      this._pending.add(key);
    }

    const loading = this._pending.size > 0;

    if (loading !== this.state.loading) {
      this.setState({ loading });
      if (this.props.onLoading) {
        this.props.onLoading(loading);
      }
    }
  };

  open = () => {
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open();
    }
  };

  updateState = (nextFiles, debugState) => {
    const files = nextFiles.filter(Boolean);
    if (files.length !== nextFiles.length) {
      logErrorToServer(new Error('Image uploading has failed'), {
        debugState,
        nextFiles
      });
    }
    this.setState({ files });
    this.props.onChange(files);
  };

  handleUpload = (files) => {
    if (files && files.length) {
      const uploadFiles = files.map((file) => ({
        file,
        preview: file.preview || URL.createObjectURL(file)
      }));
  
      const nextState = this.state.files.concat(
        fromJS(
          uploadFiles.map(({ file, preview }) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'uploading',
            preview
          }))
        )
      );
  
      this.updateState(nextState, 'pre-uploading');
  
      uploadFiles.forEach(async ({ file, preview }) => {
        this._loading(preview);
  
        const formData = new FormData();
        formData.append('files', file);
  
        try {
          const { payload } = await this.props.upload(formData);
  
          if (payload && payload.results && payload.results.length === 1) {
            const result = fromJS(payload.results[0]);
  
            return this.updateState(
              this.state.files.map((stateFile) =>
                preview === stateFile.get('preview') ? result : stateFile
              ),
              'post-uploading'
            );
          }
  
          throw new Error('Upload failed');
        } catch (err) {
          if (__DEV__) {
            console.error(err);
          }
  
          this.updateState(
            this.state.files.map((stateFile) =>
              preview === stateFile.get('preview')
                ? stateFile.set('status', 'failed')
                : stateFile
            ),
            'uploading-failed'
          );
        } finally {
          this._loading(preview, true);
        }
      });
    }
  
    return false;
  };

  handleChange = (file, action) => {
    switch (action.type) {
      case 'move': {
        const { dragIndex, hoverIndex } = action;
        return this._moveCard(dragIndex, hoverIndex);
      }
      case 'delete': {
        if (action.confirmed === true) {
          return this.updateState(
            this.state.files.filter(
              (stateFile) => file.get('filename') !== stateFile.get('filename')
            ),
            'delete'
          );
        }
        return false;
      }
      default:
        if (__DEV__) {
          console.log('handleChange', file, action);
        }
        return false;
    }
  };

  render() {
    const { admin } = this.props;
    const { files } = this.state;

    return (
      <DndProvider backend={HTML5Backend}>
        <Grid style={{ margin: '11px -11px -11px' }}>
          <Cell
            size="1/3"
            lap="1/2"
            palm="1/1"
            className={imageStyles.container}
          >
            <Dropzone
              noClick
              multiple
              ref={this.dropzoneRef}
              onDrop={this.handleUpload}
              accept={{ 'image/*': [] }}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className: 'dropzone',
                    style: {
                      display: 'table',
                      height: '100%',
                      width: '100%'
                    }
                  })}
                >
                  <input {...getInputProps()} />
                  <div
                    style={{
                      background: '#f5f5f5',
                      border: '1px #666 dashed',
                      padding: '11px',
                      textAlign: 'center',
                      display: 'table-cell',
                      verticalAlign: 'middle'
                    }}
                  >
                    <span style={{ color: '#555' }}>
                      {this.props.i18n('Drag and drop images here')}
                    </span>
                    <br />
                    <span
                      style={{
                        color: '#999',
                        display: 'inline-block',
                        margin: '11px'
                      }}
                    >
                      {this.props.i18n('- or -')}
                    </span>
                    <br />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.open}
                    >
                      {this.props.i18n('Add images')}
                    </Button>
                  </div>
                </div>
              )}
            </Dropzone>
          </Cell>

          {files.map((file, idx) => {
            return (
              <Image
                key={file.get('filename') || file.get('preview')}
                index={idx}
                file={file}
                admin={admin}
                onChange={this.handleChange}
              />
            );
          })}
        </Grid>
      </DndProvider>
    );
  }
}

export default i18n(InputImages);
